import { Select } from 'antd'
import React, { FC } from 'react'
import styled from 'styled-components'
import { KVAnnotation } from 'types'

import { g1n6DamageCategories } from './g1n6DamageTypes'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`

const CategoryButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  width: 100%;
`

const CategoryButton = styled.button<{
  $isActive: boolean
  $disabled: boolean
}>`
  padding: 0.5rem;
  border: 1px solid
    ${({ theme, $isActive }) =>
      $isActive ? theme.forecast.primary : theme.forecast.border};
  background-color: ${({ theme, $isActive }) =>
    $isActive ? theme.forecast.primary : theme.forecast.background3};
  border-radius: 4px;
  color: ${({ theme }) => theme.forecast.activeText};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.2s ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  &:hover {
    background-color: ${({ theme, $isActive, $disabled }) =>
      $disabled
        ? theme.forecast.background3
        : $isActive
          ? theme.forecast.primary
          : theme.forecast.background2};
    border-color: ${({ theme, $isActive, $disabled }) =>
      $disabled
        ? theme.forecast.border
        : $isActive
          ? theme.forecast.primary
          : theme.forecast.border};
  }
`

const StyledSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    background-color: ${({ theme }) => theme.forecast.background3} !important;
    border-color: ${({ theme }) => theme.forecast.border} !important;
    color: ${({ theme }) => theme.forecast.activeText} !important;
    font-size: 0.75rem !important;
  }

  .ant-select-selection-placeholder {
    color: ${({ theme }) => theme.forecast.secondaryText} !important;
  }

  &:hover .ant-select-selector {
    border-color: ${({ theme }) => theme.forecast.border} !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${({ theme }) => theme.forecast.primary} !important;
    box-shadow: none !important;
  }
`

const sectionOptions = [
  { value: 'overhead', label: 'Overhead' },
  { value: 'bulkheads-shell', label: 'Bulkheads/Shell' },
  { value: 'stiffeners', label: 'Stiffeners' },
  { value: 'deck-bilge', label: 'Deck/Bilge' },
]

const localizedOptions = [
  { value: 'Local', label: 'Local' },
  { value: 'Scattered', label: 'Scattered' },
]

interface G1N6DamageSelectorProps {
  annotation: KVAnnotation
  setAnnotation: (annotation: KVAnnotation) => void
}

export const G1N6DamageSelector: FC<G1N6DamageSelectorProps> = ({
  annotation,
  setAnnotation,
}) => {
  const g1n6Anno = annotation.g1n6Annotation
  const disabled = annotation.historical
  const [openDropdown, setOpenDropdown] = React.useState<
    'section' | 'component' | 'damageType' | 'localizedScattered' | null
  >(null)
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside the container
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown && containerRef.current) {
        const target = event.target as HTMLElement
        const isInsideContainer = containerRef.current.contains(target as Node)
        const isOnDropdownPortal = !!target.closest('.ant-select-dropdown')

        if (!isInsideContainer && !isOnDropdownPortal) {
          setOpenDropdown(null)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside, true) // Use capture phase
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true)
    }
  }, [openDropdown])

  if (!g1n6Anno) return null

  const activeCategory = g1n6Anno.damageCategory
  const activeCategoryData = activeCategory
    ? g1n6DamageCategories.find((cat) => cat.id === activeCategory)
    : undefined

  const handleCategoryClick = (categoryId: string) => {
    if (disabled) return

    setOpenDropdown(null) // Close any open dropdown

    setAnnotation({
      ...annotation,
      g1n6Annotation: {
        ...g1n6Anno,
        damageCategory: categoryId as
          | 'Structural'
          | 'Coating'
          | 'Outfitting'
          | '',

        component: '',
        damageType: '',
        localizedScattered: '',
      },
    })
  }

  const handleSectionChange = (value: unknown) => {
    if (typeof value !== 'string' && value !== null && value !== undefined)
      return

    setAnnotation({
      ...annotation,
      g1n6Annotation: {
        ...g1n6Anno,
        section: value || '',
      },
    })
  }

  const handleComponentChange = (value: unknown) => {
    if (typeof value !== 'string' && value !== null && value !== undefined)
      return

    setAnnotation({
      ...annotation,
      g1n6Annotation: {
        ...g1n6Anno,
        component: value || '',
      },
    })
  }

  const handleDamageTypeChange = (value: unknown) => {
    if (typeof value !== 'string' && value !== null && value !== undefined)
      return

    setAnnotation({
      ...annotation,
      g1n6Annotation: {
        ...g1n6Anno,
        damageType: value || '',
      },
    })
  }

  const handleLocalizedChange = (value: unknown) => {
    if (typeof value !== 'string' && value !== null && value !== undefined)
      return

    setAnnotation({
      ...annotation,
      g1n6Annotation: {
        ...g1n6Anno,
        localizedScattered: (value || '') as 'Local' | 'Scattered' | '',
      },
    })
  }

  const componentOptions =
    activeCategoryData?.components.map((comp) => ({
      value: comp.id,
      label: comp.label,
    })) || []

  const damageTypeOptions =
    activeCategoryData?.damageTypes.map((type) => ({
      value: type.id,
      label: type.label,
    })) || []

  const showComponentDropdown =
    activeCategory === 'Structural' || activeCategory === 'Outfitting'

  return (
    <Container ref={containerRef}>
      <CategoryButtonGrid>
        {g1n6DamageCategories.map((category) => (
          <CategoryButton
            key={category.id}
            $isActive={activeCategory === category.id}
            $disabled={disabled}
            onClick={() => handleCategoryClick(category.id)}
            type='button'>
            {category.label}
          </CategoryButton>
        ))}
      </CategoryButtonGrid>

      {activeCategory && (
        <>
          <StyledSelect
            placeholder='Select Section'
            value={g1n6Anno.section || null}
            onChange={handleSectionChange}
            disabled={disabled}
            allowClear
            options={sectionOptions}
            open={openDropdown === 'section'}
            onDropdownVisibleChange={(visible) => {
              setOpenDropdown(visible ? 'section' : null)
            }}
          />

          {showComponentDropdown && (
            <StyledSelect
              placeholder='Select Component'
              value={g1n6Anno.component || null}
              onChange={handleComponentChange}
              disabled={disabled}
              allowClear
              options={componentOptions}
              open={openDropdown === 'component'}
              onDropdownVisibleChange={(visible) => {
                setOpenDropdown(visible ? 'component' : null)
              }}
            />
          )}

          <StyledSelect
            placeholder='Select Damage Type'
            value={g1n6Anno.damageType || null}
            onChange={handleDamageTypeChange}
            disabled={disabled}
            allowClear
            options={damageTypeOptions}
            open={openDropdown === 'damageType'}
            onDropdownVisibleChange={(visible) => {
              setOpenDropdown(visible ? 'damageType' : null)
            }}
          />
          {activeCategoryData?.hasLocalizedScattered && (
            <StyledSelect
              placeholder='Select Local/Scattered'
              value={g1n6Anno.localizedScattered || null}
              onChange={handleLocalizedChange}
              disabled={disabled}
              allowClear
              options={localizedOptions}
              open={openDropdown === 'localizedScattered'}
              onDropdownVisibleChange={(visible) => {
                setOpenDropdown(visible ? 'localizedScattered' : null)
              }}
            />
          )}
        </>
      )}
    </Container>
  )
}
